/**
 * CallingHoursForm — Add/Edit calling hour window modal
 *
 * Modal form for creating or editing a CallingHourWindow.
 * Follows the same patterns as PolicyRuleForm.tsx.
 */
import { useState, useEffect } from 'react';
import { Button, Input, Label } from '@respectly/ui';
import { Switch } from '@respectly/ui';
import { Checkbox } from '@respectly/ui';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@respectly/ui';
import { Modal } from '@respectly/ui';
import { Loader2 } from 'lucide-react';
import type {
  CallingHourWindow,
  Jurisdiction,
  PREChannel,
  MessagePurpose,
} from '@respectly/shared-types';
import { usePolicyStore } from '../../stores/usePolicyStore';
import { buildCallingHoursRuleDto } from '../../lib/callingHoursRule';

// ============================================
// Types
// ============================================

interface CallingHoursFormProps {
  isOpen: boolean;
  onClose: () => void;
  window?: CallingHourWindow | null;
  jurisdictions: Jurisdiction[];
}

interface FormState {
  name: string;
  windowId: string;
  jurisdictionCode: string;
  channel: PREChannel | '';
  messagePurpose: MessagePurpose | '';
  startHour: number;
  startMinute: number;
  endHour: number;
  endMinute: number;
  daysOfWeek: number[];
  timezone: string;
  sourceRef: string;
  isActive: boolean;
  /** When true, saving also creates/ensures the enforcing CONDITION rule. */
  enforce: boolean;
}

// ============================================
// Constants
// ============================================

const CHANNEL_OPTIONS = [
  { value: '', label: 'All Channels' },
  { value: 'VOICE', label: 'Voice' },
  { value: 'SMS', label: 'SMS' },
  { value: 'EMAIL', label: 'Email' },
];

const PURPOSE_OPTIONS = [
  { value: '', label: 'All Purposes' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'INFORMATIONAL', label: 'Informational' },
];

const DAYS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = [0, 15, 30, 45];

function padHour(h: number): string {
  return String(h).padStart(2, '0');
}

function getDefaultForm(): FormState {
  return {
    name: '',
    windowId: '',
    jurisdictionCode: '',
    channel: '',
    messagePurpose: '',
    startHour: 8,
    startMinute: 0,
    endHour: 20,
    endMinute: 0,
    daysOfWeek: [1, 2, 3, 4, 5],
    timezone: 'recipient-local',
    sourceRef: '',
    isActive: true,
    enforce: true,
  };
}

// ============================================
// Component
// ============================================

export function CallingHoursForm({
  isOpen,
  onClose,
  window: existingWindow,
  jurisdictions,
}: CallingHoursFormProps) {
  const createCallingHourWindow = usePolicyStore((s) => s.createCallingHourWindow);
  const updateCallingHourWindow = usePolicyStore((s) => s.updateCallingHourWindow);
  const createRule = usePolicyStore((s) => s.createRule);
  const loadCallingHourRuleRefs = usePolicyStore((s) => s.loadCallingHourRuleRefs);
  const referencedWindowIds = usePolicyStore((s) => s.referencedWindowIds);
  const isCreating = usePolicyStore((s) => s.isCreating);
  const isUpdating = usePolicyStore((s) => s.isUpdating);

  const isEdit = !!existingWindow;
  const isSaving = isCreating || isUpdating;

  const [form, setForm] = useState<FormState>(getDefaultForm());
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});

  // Is this (existing) window already activated by an enforcing rule?
  const alreadyEnforced = existingWindow
    ? referencedWindowIds.includes(existingWindow.windowId)
    : false;

  // Populate form when editing
  useEffect(() => {
    if (existingWindow) {
      setForm({
        name: existingWindow.name,
        windowId: existingWindow.windowId,
        jurisdictionCode: existingWindow.jurisdictionCode,
        channel: existingWindow.channel ?? '',
        messagePurpose: existingWindow.messagePurpose ?? '',
        startHour: existingWindow.startHour,
        startMinute: existingWindow.startMinute,
        endHour: existingWindow.endHour,
        endMinute: existingWindow.endMinute,
        daysOfWeek: [...existingWindow.daysOfWeek],
        timezone: existingWindow.timezone,
        sourceRef: existingWindow.sourceRef ?? '',
        isActive: existingWindow.isActive,
        // Reflect whether a rule already references this window.
        enforce: referencedWindowIds.includes(existingWindow.windowId),
      });
    } else {
      setForm(getDefaultForm());
    }
    setErrors({});
  }, [existingWindow, isOpen, referencedWindowIds]);

  // Auto-generate windowId for new windows
  useEffect(() => {
    if (!isEdit && form.jurisdictionCode) {
      const code = form.jurisdictionCode.replace(/-/g, '_');
      const start = padHour(form.startHour);
      const end = padHour(form.endHour);
      setForm((prev) => ({
        ...prev,
        windowId: `CUSTOM_${code}_${start}_${end}`,
      }));
    }
  }, [isEdit, form.jurisdictionCode, form.startHour, form.endHour]);

  const validate = (): boolean => {
    const newErrors: Partial<Record<string, string>> = {};
    if (!form.name.trim()) newErrors.name = 'Name is required';
    if (!form.jurisdictionCode) newErrors.jurisdictionCode = 'Jurisdiction is required';
    const startTotal = form.startHour * 60 + form.startMinute;
    const endTotal = form.endHour * 60 + form.endMinute;
    if (startTotal >= endTotal) newErrors.time = 'Start time must be before end time';
    if (form.daysOfWeek.length === 0) newErrors.days = 'At least one day is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const dto = {
      name: form.name.trim(),
      windowId: form.windowId,
      jurisdictionCode: form.jurisdictionCode,
      channel: (form.channel || null) as PREChannel | null,
      messagePurpose: (form.messagePurpose || null) as MessagePurpose | null,
      startHour: form.startHour,
      startMinute: form.startMinute,
      endHour: form.endHour,
      endMinute: form.endMinute,
      daysOfWeek: form.daysOfWeek,
      timezone: form.timezone || 'recipient-local',
      sourceRef: form.sourceRef.trim() || null,
      isActive: form.isActive,
    };

    // Create the enforcing CONDITION rule from the window's own fields. Used by
    // both the create and edit paths (edit only when not already enforced).
    const ensureEnforcementRule = async () => {
      await createRule(
        buildCallingHoursRuleDto({
          windowId: dto.windowId,
          name: dto.name,
          jurisdictionCode: dto.jurisdictionCode,
          channel: dto.channel,
          messagePurpose: dto.messagePurpose,
          sourceRef: dto.sourceRef,
          isActive: dto.isActive,
        }),
      );
      await loadCallingHourRuleRefs();
    };

    if (isEdit && existingWindow) {
      const success = await updateCallingHourWindow(existingWindow.id, dto);
      if (success) {
        // Activate the window if the user turned enforcement on and no rule
        // references it yet (never create a duplicate).
        if (form.enforce && !alreadyEnforced) await ensureEnforcementRule();
        onClose();
      }
    } else {
      const id = await createCallingHourWindow(dto);
      if (id) {
        if (form.enforce) await ensureEnforcementRule();
        onClose();
      }
    }
  };

  const toggleDay = (day: number) => {
    setForm((prev) => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter((d) => d !== day)
        : [...prev.daysOfWeek, day].sort(),
    }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="large"
      title={isEdit ? `Edit Window: ${existingWindow?.windowId}` : 'Add Calling Hour Window'}
    >
      <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
        {/* Name */}
        <div className="space-y-1.5">
          <Label htmlFor="ch-name">Name *</Label>
          <Input
            id="ch-name"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            placeholder="e.g. Florida FTSA (8am-8pm)"
          />
          {errors.name && (
            <p className="text-xs text-red-500">{errors.name}</p>
          )}
        </div>

        {/* Window ID (read-only for edit, auto-generated for new) */}
        <div className="space-y-1.5">
          <Label htmlFor="ch-windowId">Window ID</Label>
          <Input
            id="ch-windowId"
            value={form.windowId}
            readOnly
            className="font-mono text-sm bg-slate-50 dark:bg-slate-800"
          />
        </div>

        {/* Jurisdiction */}
        <div className="space-y-1.5">
          <Label>Jurisdiction *</Label>
          <Select
            value={form.jurisdictionCode}
            onValueChange={(v) => setForm((p) => ({ ...p, jurisdictionCode: v }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select jurisdiction" />
            </SelectTrigger>
            <SelectContent>
              {jurisdictions.map((j) => (
                <SelectItem key={j.code} value={j.code}>
                  {j.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.jurisdictionCode && (
            <p className="text-xs text-red-500">{errors.jurisdictionCode}</p>
          )}
        </div>

        {/* Channel + Purpose */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Channel</Label>
            <Select
              value={form.channel}
              onValueChange={(v) =>
                setForm((p) => ({ ...p, channel: v as PREChannel | '' }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="All Channels" />
              </SelectTrigger>
              <SelectContent>
                {CHANNEL_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value || '_all'} value={opt.value || '_all'}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Message Purpose</Label>
            <Select
              value={form.messagePurpose}
              onValueChange={(v) =>
                setForm((p) => ({ ...p, messagePurpose: v as MessagePurpose | '' }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="All Purposes" />
              </SelectTrigger>
              <SelectContent>
                {PURPOSE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value || '_all'} value={opt.value || '_all'}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Start / End Time */}
        <div className="space-y-1.5">
          <Label>Time Window *</Label>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <span className="text-xs text-slate-500 dark:text-slate-400">Start Time</span>
              <div className="flex items-center gap-2">
                <Select
                  value={String(form.startHour)}
                  onValueChange={(v) => setForm((p) => ({ ...p, startHour: Number(v) }))}
                >
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HOURS.map((h) => (
                      <SelectItem key={h} value={String(h)}>
                        {padHour(h)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-slate-400">:</span>
                <Select
                  value={String(form.startMinute)}
                  onValueChange={(v) => setForm((p) => ({ ...p, startMinute: Number(v) }))}
                >
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MINUTES.map((m) => (
                      <SelectItem key={m} value={String(m)}>
                        {String(m).padStart(2, '0')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <span className="text-xs text-slate-500 dark:text-slate-400">End Time</span>
              <div className="flex items-center gap-2">
                <Select
                  value={String(form.endHour)}
                  onValueChange={(v) => setForm((p) => ({ ...p, endHour: Number(v) }))}
                >
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HOURS.map((h) => (
                      <SelectItem key={h} value={String(h)}>
                        {padHour(h)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-slate-400">:</span>
                <Select
                  value={String(form.endMinute)}
                  onValueChange={(v) => setForm((p) => ({ ...p, endMinute: Number(v) }))}
                >
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MINUTES.map((m) => (
                      <SelectItem key={m} value={String(m)}>
                        {String(m).padStart(2, '0')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          {errors.time && (
            <p className="text-xs text-red-500">{errors.time}</p>
          )}
        </div>

        {/* Days of Week */}
        <div className="space-y-1.5">
          <Label>Days of Week *</Label>
          <div className="flex flex-wrap gap-3">
            {DAYS.map((day) => (
              <Checkbox
                key={day.value}
                label={day.label}
                checked={form.daysOfWeek.includes(day.value)}
                onCheckedChange={() => toggleDay(day.value)}
              />
            ))}
          </div>
          {errors.days && (
            <p className="text-xs text-red-500">{errors.days}</p>
          )}
        </div>

        {/* Timezone + Source */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="ch-timezone">Timezone</Label>
            <Input
              id="ch-timezone"
              value={form.timezone}
              onChange={(e) => setForm((p) => ({ ...p, timezone: e.target.value }))}
              placeholder="recipient-local"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ch-source">Source Reference</Label>
            <Input
              id="ch-source"
              value={form.sourceRef}
              onChange={(e) => setForm((p) => ({ ...p, sourceRef: e.target.value }))}
              placeholder="Legal citation (optional)"
            />
          </div>
        </div>

        {/* Active toggle */}
        <div className="flex items-center justify-between py-2">
          <Label>Active</Label>
          <Switch
            checked={form.isActive}
            onCheckedChange={(checked) =>
              setForm((p) => ({ ...p, isActive: !!checked }))
            }
          />
        </div>

        {/* Enforcement — auto-create the rule that activates this window */}
        <div className="rounded-lg border border-amber-200 dark:border-amber-900/40 bg-amber-50/60 dark:bg-amber-900/10 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5 pr-4">
              <Label>Enforce these hours</Label>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Creates a rule that defers out-of-hours sends to the next allowed window.
                A window does nothing on its own until a rule activates it.
              </p>
            </div>
            <Switch
              checked={alreadyEnforced || form.enforce}
              disabled={alreadyEnforced}
              onCheckedChange={(checked) =>
                setForm((p) => ({ ...p, enforce: !!checked }))
              }
            />
          </div>
          {alreadyEnforced && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              Already activated by an existing rule — manage it in Jurisdiction Rules → Sending Times.
            </p>
          )}
          {!alreadyEnforced && (form.enforce && !form.messagePurpose) && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              Purpose is set to “All”, so the rule will also gate Informational sends — and these
              will be blocked when the recipient timezone can’t be determined.
            </p>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
        <Button variant="outline" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={isSaving}>
          {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {isEdit ? 'Update Window' : 'Create Window'}
        </Button>
      </div>
    </Modal>
  );
}
